import { Controller } from '@hotwired/stimulus'
import { getBase64Strings } from 'exif-rotate-js'
import { sendError } from '../utils'

/**
 * TODO: cleanup label and loader targets and all class manipulations on new layout
 *  - Loader must be based on aria-busy if we need one.
 *  - Placeholder must be based on data-preview attribute.
 */
export default class extends Controller {
  static targets = ['input', 'label', 'preview', 'loader', 'clearButton', 'removeCheckbox']
  static classes = ['loaded', 'loading', 'hidden']

  // État du champ au chargement de la page, restauré par `clear`.
  #initialPreviewSrc = null
  #initialPreviewAlt = null
  #initialLabelClass = null

  connect() {
    if (this.hasPreviewTarget) {
      this.#initialPreviewSrc = this.previewTarget.getAttribute('src')
      this.#initialPreviewAlt = this.previewTarget.getAttribute('alt')
    }

    if (this.hasLabelTarget) this.#initialLabelClass = this.labelTarget.className

    // L'aperçu est un état transient : capturé tel quel par le cache de
    // snapshots Turbo, il réapparaissait un instant en rouvrant le formulaire
    // (le snapshot sert d'aperçu avant le rendu frais). Nettoyé avant capture.
    this.beforeCache = () => this.clear()
    document.addEventListener('turbo:before-cache', this.beforeCache)
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.beforeCache)
  }

  /**
   * Annule le fichier choisi et revient à l'état du chargement de la page : l'image
   * déjà enregistrée s'il y en avait une, sinon le placeholder vide. Supprimer une
   * image enregistrée reste le rôle de la case `remove_cover`, qui agit au submit.
   */
  clear() {
    this.inputTarget.value = ''
    delete this.element.dataset.preview

    if (this.hasPreviewTarget) {
      if (this.#initialPreviewSrc) {
        this.previewTarget.src = this.#initialPreviewSrc
      } else {
        this.previewTarget.removeAttribute('src')
      }

      this.previewTarget.setAttribute('alt', this.#initialPreviewAlt || '')
    }

    if (this.hasLabelTarget && this.#initialLabelClass !== null) {
      this.labelTarget.className = this.#initialLabelClass
    }

    this.#hideClearButton()
  }

  /** Entrée « choisir une photo » du menu : relaie vers le champ fichier caché. */
  pickFile() {
    this.inputTarget.click()
  }

  /**
   * « Supprimer la photo » : bascule sur le placeholder et coche la case
   * remove_* cachée — l'effacement réel reste au submit. Choisir un nouveau
   * fichier la décoche (display).
   */
  remove() {
    this.inputTarget.value = ''
    delete this.element.dataset.preview
    if (this.hasPreviewTarget) {
      // Pixel transparent + alt vide : sans src, l'<img> affiche son texte
      // alternatif (le nom de la liste apparaissait dans le slot).
      this.previewTarget.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
      this.previewTarget.setAttribute('alt', '')
    }
    if (this.hasRemoveCheckboxTarget) this.removeCheckboxTarget.checked = true
    // Layouts à classes d'état (uploader de liste) : le slot redevient vide.
    if (this.hasLabelTarget) this.labelTarget.classList.remove(...this.loadedClasses)
  }

  display(_event) {
    const file = this.inputTarget.files && this.inputTarget.files[0]

    // If no file selected (user cancelled), do nothing
    if (!file) return
    if (this.hasRemoveCheckboxTarget) this.removeCheckboxTarget.checked = false

    if (this.#isValid(file)) {
      this.#showLoader()
      this.element.ariaBusy = ''

      getBase64Strings(this.inputTarget.files, { maxSize: 1024, type: file.type })
        .then((data) => {
          this.#hideImagePlaceholder()
          this.element.dataset.preview = ''
          this.previewTarget.src = data[0]
          this.previewTarget.setAttribute('alt', file.name)
          this.#showClearButton()
        })
        .catch((error) => {
          console.error(error)
          sendError(error, this.identifier, { type: file.type })
        })
        .finally(() => {
          this.element.ariaBusy = undefined
          this.#hideLoader()
        })
    } else {
      // File is invalid (wrong type), remove preview
      this.previewTarget.removeAttribute('src')
      this.previewTarget.setAttribute('alt', file.name)
    }
  }

  #isValid(file) {
    if (file === null || file === undefined) return false

    const mimetypes = this.inputTarget.accept.split(',').map((type) => type.trim())
    return mimetypes.includes(file.type)
  }

  #hideImagePlaceholder() {
    if (!this.hasLabelTarget || !this.hasLoadedClass) return

    // `loadedClasses` (plural) : l'attribut en déclare plusieurs, le singulier ne rendrait
    // que la première et `is-loaded` — qui masque le placeholder — serait perdue.
    this.labelTarget.classList.add(...this.loadedClasses)
  }

  #showClearButton() {
    if (!this.hasClearButtonTarget || !this.hasHiddenClass) return

    this.clearButtonTarget.classList.remove(this.hiddenClass)
  }

  #hideClearButton() {
    if (!this.hasClearButtonTarget || !this.hasHiddenClass) return

    this.clearButtonTarget.classList.add(this.hiddenClass)
  }

  #showLoader() {
    if (!this.hasLoaderTarget) return

    this.loaderTarget.classList.remove(this.loadingClass)
  }

  #hideLoader() {
    if (!this.hasLoaderTarget) return

    this.loaderTarget.classList.add(this.loadingClass)
  }
}
